import { useMemo, useSyncExternalStore } from 'react'

type UnsupportedBrowser = { id: string; label: string; detect: () => boolean }

/**
 * Private registry of niche/unsupported browsers.
 * Add a new entry here to extend detection — no other files need to change.
 */
const UNSUPPORTED_BROWSERS: UnsupportedBrowser[] = [
  {
    id: 'arc',
    label: 'Arc',
    // Arc injects its own CSS custom properties onto :root; a non-empty value
    // is the most reliable way to detect it without UA sniffing.
    detect: () =>
      !!getComputedStyle(document.documentElement)
        .getPropertyValue('--arc-palette-title')
        .trim(),
  },
  {
    id: 'dia',
    label: 'Dia',
    detect: () => /\bDia\//i.test(navigator.userAgent),
  },
]

// ---------------------------------------------------------------------------
// Module-level dismissed store — shared across all hook instances so that
// dismissing from the banner immediately removes the sidebar offset without
// needing a context or prop drilling.
// ---------------------------------------------------------------------------

const DISMISS_KEY = 'humblytics:browser-warning:dismissed:v1'
const DISMISS_TTL_DAYS = 30

function readDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const expires = parseInt(raw, 10)
    if (!Number.isFinite(expires) || Date.now() > expires) {
      localStorage.removeItem(DISMISS_KEY)
      return false
    }
    return true
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    const expiresAt = Date.now() + DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000
    localStorage.setItem(DISMISS_KEY, String(expiresAt))
  } catch {
    // ignore — storage may be unavailable
  }
}

let _dismissed = readDismissed()
const _listeners = new Set<() => void>()

function subscribeListeners(fn: () => void) {
  _listeners.add(fn)
  return () => { _listeners.delete(fn) }
}

function getDismissed() {
  return _dismissed
}

/** Persists dismissal and notifies all hook instances synchronously. */
function dismissBrowserWarning() {
  writeDismissed()
  _dismissed = true
  _listeners.forEach(fn => fn())
}

// ---------------------------------------------------------------------------
// Module-level browser detection — runs once at module load time (client only)
// and is shared across every hook instance. This means all consumers always
// agree on whether a niche browser was detected, regardless of when they mount.
// ---------------------------------------------------------------------------

const _match: UnsupportedBrowser | null = (() => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null
  return (
    UNSUPPORTED_BROWSERS.find(b => {
      try { return b.detect() } catch { return false }
    }) ?? null
  )
})()

const BANNER_HEIGHT = 45

// ---------------------------------------------------------------------------

type BrowserSupportResult = {
  /** True when no unsupported browser was detected. */
  isSupported: boolean
  /** Matched niche browser, or null when the browser is supported. */
  browser: { id: string; label: string } | null
  /** True when the browser is unsupported AND the user has not dismissed the notice. */
  isBannerVisible: boolean
  /** Height (px) to reserve for the banner. 0 when the banner is not visible. */
  bannerHeight: number
  /** Dismisses the notice app-wide and persists the decision for 30 days. */
  dismiss: () => void
}

/**
 * Detects whether the current browser is in the unsupported registry and
 * tracks the dismissal state reactively across all mounted consumers.
 *
 * Detection runs once per session — browsers cannot change mid-session, so the
 * result is stable. Returns `{ isSupported: true, isBannerVisible: false }` for
 * all supported browsers, ensuring zero runtime cost on the happy path.
 */
export function useBrowserSupport(): BrowserSupportResult {
  // Dismissal is the only thing that changes at runtime — subscribe reactively.
  const isDismissed = useSyncExternalStore(
    subscribeListeners,
    getDismissed,
    () => false,
  )

  const isBannerVisible = !!_match && !isDismissed

  return useMemo(() => ({
    isSupported: !_match,
    browser: _match ? { id: _match.id, label: _match.label } : null,
    isBannerVisible,
    bannerHeight: isBannerVisible ? BANNER_HEIGHT : 0,
    dismiss: dismissBrowserWarning,
  }), [isBannerVisible])
}
